// A `ShellHost` over a native app's web view, found on the window.
//
// Promise:
// - To the app: iOS (WKWebView) gets each message as an object through
//   `window.webkit.messageHandlers.trameriaShell.postMessage`; Android
//   (WebView) gets it as JSON text through `window.trameriaShell.postMessage`.
// - From the app: it dispatches `new CustomEvent("trameria-shell", { detail })`
//   on the window, `detail` a message object (or its JSON text). Messages this
//   version does not know are ignored.
// - What the app says of itself (the floating parts it draws, its own
//   notifications) is read once,
//   from `window.trameriaShellHost` (`ShellHostInfo`), which the app sets
//   before the screens' first script. Absent: it draws none of them.
// - Found: the page is marked `html[data-shell-host]`, which the styles read
//   (no focus marks inside an app: index.css).
// - No handler on the window: null (a plain browser; the web draws its frame).
import { m } from "@trameria/i18n";
import {
  readFromHost,
  readShellHostInfo,
  SHELL_CONTRACT_VERSION,
  type ShellMessageFromHost,
  type ShellMessageToHost,
} from "./contract";
import type { ShellHost } from "./outside";

/** The event the app dispatches on the window. */
export const SHELL_EVENT = "trameria-shell";

interface NativeWindow {
  webkit?: {
    messageHandlers?: {
      trameriaShell?: { postMessage(message: unknown): void };
    };
  };
  trameriaShell?: { postMessage(message: string): void };
  trameriaShellHost?: unknown;
}

export function createWindowShellHost(
  win: Window,
  options: { embedded?: boolean } = {},
): ShellHost | null {
  const native = win as unknown as NativeWindow;
  const ios = native.webkit?.messageHandlers?.trameriaShell;
  const android = native.trameriaShell;
  const post = ios
    ? (m: ShellMessageToHost) => ios.postMessage(m)
    : android
      ? (m: ShellMessageToHost) => android.postMessage(JSON.stringify(m))
      : null;
  if (!post) return null;
  win.document?.documentElement.setAttribute("data-shell-host", "");
  const info = readShellHostInfo(native.trameriaShellHost);
  return {
    embedded: options.embedded ?? true,
    floats: new Set(info?.floats ?? []),
    ...(info?.phone ? { phone: true } : {}),
    ...(info?.notices ? { notices: info.notices } : {}),
    ...(info?.appSettings ? { appSettings: true } : {}),
    ...(info?.viewer ? { viewer: true } : {}),
    post,
    subscribe(listener: (message: ShellMessageFromHost) => void) {
      const onEvent = (e: Event) => {
        let detail = (e as CustomEvent<unknown>).detail;
        if (typeof detail === "string") {
          try {
            detail = JSON.parse(detail);
          } catch {
            return;
          }
        }
        const message = readFromHost(detail);
        if (message) listener(message);
      };
      win.addEventListener(SHELL_EVENT, onEvent);
      return () => win.removeEventListener(SHELL_EVENT, onEvent);
    },
  };
}

/**
 * The host's own settings (`ShellHostInfo.appSettings`, the iOS app's), as
 * the settings' `appSettings`: one row under "This app" that opens them, in
 * place of this app's items (the look, the keys), which the host keeps for
 * all its screens. Undefined when the host has none.
 */
export function hostAppSettings(
  host: ShellHost | null | undefined,
): { open(): void; description(): string } | undefined {
  if (!host?.appSettings) return undefined;
  return {
    open: () =>
      host.post({ version: SHELL_CONTRACT_VERSION, type: "app-settings" }),
    description: () => m.settings_app_open_description_device(),
  };
}
